import React, { useMemo } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { Box, Typography, Divider, Stack } from "@mui/material";

const WIDTH = 400;
const HEIGHT = 260;
const CX = WIDTH / 2;
const CY = 200;
const INCOME_INNER_R = 80;
const INCOME_OUTER_R = 110;
const EXPENSE_INNER_R = 120;
const EXPENSE_OUTER_R = 160;
const INCOME_COLOR = "#1e3a5f";
const EXPENSE_COLOR = "#d4af37";

const Gauge = ({ incomeData = [], expensesData = [], title = "תמונת מצב חודשית", showTitle = false }) => {
    const { totalIncome, totalExpenses, balance, isPositive, maxValue } = useMemo(() => {
        const tIncome = incomeData.reduce((acc, curr) => acc + curr.value, 0);
        const tExpenses = expensesData.reduce((acc, curr) => acc + curr.value, 0);
        const maxVal = Math.max(tExpenses, tIncome);
        return {
            totalIncome: tIncome,
            totalExpenses: tExpenses,
            balance: tIncome - tExpenses,
            isPositive: (tIncome - tExpenses) >= 0,
            maxValue: maxVal > 0 ? maxVal : 1
        };
    }, [incomeData, expensesData]);

    const processedIncome = useMemo(() => {
        const data = [...incomeData];
        if (totalIncome < maxValue) {
            data.push({
                id: "ghost_income",
                value: maxValue - totalIncome,
                color: "url(#missingPattern)",
                label: "חסר ליעד",
                tooltip: "חסר ליעד"
            });
        }
        return data;
    }, [incomeData, totalIncome, maxValue]);

    const processedExpenses = useMemo(() => {
        const data = [...expensesData];
        if (totalExpenses < maxValue) {
            data.push({
                id: "ghost_expense",
                value: maxValue - totalExpenses,
                color: "rgba(0,0,0,0)",
                tooltip: "none"
            });
        }
        return data;
    }, [expensesData, totalExpenses, maxValue]);

    const needleAngle = useMemo(() => {
        const ratio = totalIncome / maxValue;
        const safeRatio = Math.min(ratio, 1);
        return -90 + (safeRatio * 180);
    }, [totalIncome, maxValue]);

    return (
        <Box sx={{ width: "100%", height: "100%", fontSize: 'inherit', p: '1.5em' }}>
            {showTitle && (
                <Typography
                    variant="h6"
                    sx={{
                        textAlign: 'center',
                        mb: '1em',
                        fontSize: '1.2em',
                        fontWeight: 'bold',
                        color: '#1e3a5f'
                    }}
                >
                    {title}
                </Typography>
            )}
            <svg style={{ height: 0, width: 0, position: 'absolute' }}>
                <defs>
                    <pattern id="missingPattern" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
                        <rect width="10" height="10" fill="#f0f0f0" />
                        <line x1="0" y1="0" x2="0" y2="10" stroke="#bdbdbd" strokeWidth="4" />
                    </pattern>
                    <filter id="needleShadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.3)" />
                    </filter>
                </defs>
            </svg>

            <Box sx={{ position: "relative", width: WIDTH, height: HEIGHT, margin: '0 auto' }}>
                <PieChart
                    width={WIDTH}
                    height={WIDTH}
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={{
                        '& .MuiChartsLegend-root': { display: 'none' }
                    }}
                    series={[
                        {
                            data: processedIncome,
                            innerRadius: INCOME_INNER_R,
                            outerRadius: INCOME_OUTER_R,
                            startAngle: -90,
                            endAngle: 90,
                            cx: CX,
                            cy: CY,
                            paddingAngle: 0,
                            cornerRadius: 2,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                        },
                        {
                            data: processedExpenses,
                            innerRadius: EXPENSE_INNER_R,
                            outerRadius: EXPENSE_OUTER_R,
                            startAngle: -90,
                            endAngle: 90,
                            cx: CX,
                            cy: CY,
                            paddingAngle: 2,
                            cornerRadius: 4
                        }
                    ]}
                />

                <svg width={WIDTH} height={HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}>
                    <g
                        style={{
                            transformOrigin: `${CX}px ${CY}px`,
                            transform: `rotate(${needleAngle}deg)`,
                            transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        <path
                            d={`M ${CX - 7} ${CY} L ${CX} ${CY - INCOME_OUTER_R - 5} L ${CX + 7} ${CY} Z`}
                            fill={INCOME_COLOR}
                            filter="url(#needleShadow)"
                            stroke="white"
                            strokeWidth="1"
                        />
                        <circle cx={CX} cy={CY} r="10" fill="#fff" stroke={INCOME_COLOR} strokeWidth="4" filter="url(#needleShadow)" />
                    </g>
                </svg>

                <Box
                    sx={{
                        position: "absolute",
                        top: CY + 30,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        zIndex: 10
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight="900"
                        color={isPositive ? "#2e7d32" : "#d32f2f"}
                        sx={{ direction: "ltr", lineHeight: 1.1, fontSize: '2em' }}
                    >
                        {balance > 0 ? "+" : ""}{balance.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '1.2em' }}>
                        אנא תרמו בעין יפה
                        <br /> וסייעו בהחזקת בית המדרש
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ mt: '5em', mb: '1.5em' }} />

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '2em', direction: 'rtl' }}>
                <Box sx={{ minWidth: '12em' }}>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color={INCOME_COLOR}
                        sx={{ mb: '1em', textAlign: 'center', fontSize: '0.875em' }}
                    >
                        הכנסות
                    </Typography>
                    <Stack spacing={0.75}>
                        {incomeData.map((item) => (
                            <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                    <Box sx={{ width: '0.75em', height: '0.75em', borderRadius: '0.25em', bgcolor: item.color }} />
                                    <Typography variant="body2" color="text.primary" sx={{ fontSize: '1em' }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1em' }}>
                                    ₪{item.value.toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                        {!isPositive && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: '0.5em',
                                    p: '0.5em',
                                    bgcolor: '#f5f5f5',
                                    borderRadius: '0.5em',
                                    border: '0.0625em dashed #bdbdbd'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1em' }}>
                                    מאזן נוכחי
                                </Typography>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '1em' }}>
                                    <span style={{ direction: 'ltr', display: 'inline-block' }}>₪{(balance).toLocaleString()}</span>
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>

                <Box sx={{ minWidth: '12em' }}>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color={EXPENSE_COLOR}
                        sx={{ mb: '1em', textAlign: 'center', fontSize: '0.875em' }}
                    >
                        הוצאות
                    </Typography>
                    <Stack spacing={0.75}>
                        {expensesData.map((item) => (
                            <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                    <Box sx={{ width: '0.75em', height: '0.75em', borderRadius: '0.25em', bgcolor: item.color }} />
                                    <Typography variant="body2" color="text.primary" sx={{ fontSize: '1em' }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1em' }}>
                                    ₪{item.value.toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

export default Gauge;
